/**
 * Drawer "Générer les bons en masse" (Multi-Bons V1 — 14/02/2026)
 *
 * Permet de générer N bons de chargement (et optionnellement déchargement)
 * pour une opération multi-camions / multi-rotations en un seul clic.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - operationId: string
 *  - operationWeight?: number (poids total, pour suggestion split auto)
 *  - onGenerated?: (result) => void (callback succès — utile pour refresh)
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Truck, Loader2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BulkGenerateNotesDrawer = ({ open, onClose, operationId, operationWeight, onGenerated }) => {
  const [assignments, setAssignments] = useState([]);
  const [rows, setRows] = useState([]); // [{ assignmentIndex, rotationsPlanned, plannedWeight, label }]
  const [generateLoading, setGenerateLoading] = useState(true);
  const [generateUnloading, setGenerateUnloading] = useState(false);
  const [pairBcBd, setPairBcBd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const fetchAssignments = useCallback(async () => {
    if (!operationId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/operations/${operationId}/logistics`);
      const list = Array.isArray(r.data) ? r.data : [];
      // Cherche le doc avec assignments multi-camions
      const withAssignments = list.find(l => Array.isArray(l.assignments) && l.assignments.length > 0);
      const rawAssignments = withAssignments?.assignments || [];

      // Fallback singleton si aucun multi-camion configuré
      const finalList = rawAssignments.length > 0
        ? rawAssignments
        : [{ index: 0, driverName: null, vehiclePlate: null, vehicleType: null }];

      setAssignments(finalList);
      // Suggestion auto : si vehicleCapacity dispo, calcule rotations = ceil(part_tonnage / capacity)
      const totalCapacity = finalList.reduce((s, a) => {
        const cap = parseFloat(a.vehicleCapacity || a.capacity || 0);
        return s + (isFinite(cap) && cap > 0 ? cap : 0);
      }, 0);
      const totalWeight = parseFloat(operationWeight || 0);
      setRows(finalList.map((a, idx) => {
        const idxSafe = typeof a.index === 'number' ? a.index : idx;
        const cap = parseFloat(a.vehicleCapacity || a.capacity || 0);
        // Part de tonnage allouée à ce camion = capacité / capacité totale (à défaut, parts égales)
        const shareWeight = (totalCapacity > 0 && cap > 0 && totalWeight > 0)
          ? Number(((totalWeight * cap) / totalCapacity).toFixed(2))
          : (totalWeight > 0 ? Number((totalWeight / finalList.length).toFixed(2)) : 0);
        // Rotations suggérées : si capacité connue et shareWeight > capacité → ceil(shareWeight / cap)
        const suggestedRotations = (cap > 0 && shareWeight > 0)
          ? Math.max(1, Math.ceil(shareWeight / cap))
          : 1;
        const plannedPerTrip = suggestedRotations > 0 && shareWeight > 0
          ? Number((shareWeight / suggestedRotations).toFixed(2))
          : (totalWeight > 0 ? Number((totalWeight / finalList.length).toFixed(2)) : '');
        return {
          assignmentIndex: idxSafe,
          rotationsPlanned: suggestedRotations,
          plannedWeight: plannedPerTrip,
          label: `${a.vehiclePlate || 'Sans plaque'} — ${a.driverName || 'Sans chauffeur'}${cap > 0 ? ` (cap. ${cap}T)` : ''}`,
        };
      }));
    } catch (err) {
      console.error('Failed to load logistics', err);
      setError("Impossible de charger les camions assignés. Assurez-vous que la logistique est planifiée.");
    } finally {
      setLoading(false);
    }
  }, [operationId, operationWeight]);

  useEffect(() => {
    if (open) fetchAssignments();
  }, [open, fetchAssignments]);

  const totalBons = rows.reduce((sum, r) => {
    const types = (generateLoading ? 1 : 0) + (generateUnloading ? 1 : 0);
    return sum + (r.rotationsPlanned || 0) * types;
  }, 0);

  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const handleSubmit = async () => {
    if (!generateLoading && !generateUnloading) {
      toast.error("Sélectionnez au moins un type (BC ou BD)");
      return;
    }
    const validRows = rows.filter(r => Number(r.rotationsPlanned) > 0);
    if (validRows.length === 0) {
      toast.error("Aucune rotation à générer");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        rotations: validRows.map(r => ({
          assignmentIndex: Number(r.assignmentIndex),
          rotationsPlanned: Number(r.rotationsPlanned),
          plannedWeight: r.plannedWeight ? Number(r.plannedWeight) : null,
        })),
        generateLoading,
        generateUnloading,
        pairBcBd: pairBcBd && generateLoading && generateUnloading,
      };
      const r = await axios.post(`${API}/operations/${operationId}/loading-notes/generate-bulk`, payload);
      const { createdCount, skippedCount } = r.data;
      if (createdCount > 0) {
        toast.success(`${createdCount} bon(s) créé(s)${skippedCount > 0 ? ` — ${skippedCount} déjà existant(s) ignoré(s)` : ''}`);
      } else if (skippedCount > 0) {
        toast.info(`${skippedCount} bon(s) déjà existant(s) — rien à créer`);
      }
      onGenerated?.(r.data);
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors de la génération en masse";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="bulk-generate-notes-drawer">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="ml-auto w-full max-w-2xl bg-white shadow-2xl h-full flex flex-col relative">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-emerald-600" />
            <h2 className="text-lg font-semibold text-gray-900">Générer les bons en masse</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="bulk-drawer-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement des camions assignés...
            </div>
          ) : error ? (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          ) : (
            <>
              <div className="text-sm text-gray-600">
                Configure le nombre de voyages (rotations) par camion. Chaque rotation génère 1 bon
                {generateLoading && generateUnloading ? ' BC + 1 BD' : generateLoading ? ' BC' : ' BD'}.
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Camion / Chauffeur</th>
                      <th className="px-3 py-2 text-center font-medium w-28">Rotations</th>
                      <th className="px-3 py-2 text-center font-medium w-32">Tonnage / voyage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => (
                      <tr key={idx} className="border-t border-gray-100">
                        <td className="px-3 py-2 text-gray-800">{r.label}</td>
                        <td className="px-3 py-2">
                          <input
                            type="number" min="0" max="20" step="1"
                            value={r.rotationsPlanned}
                            onChange={e => updateRow(idx, 'rotationsPlanned', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-center"
                            data-testid={`bulk-rotations-${idx}`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number" min="0" step="0.1"
                            value={r.plannedWeight}
                            onChange={e => updateRow(idx, 'plannedWeight', e.target.value)}
                            placeholder="ex: 15"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-center"
                            data-testid={`bulk-weight-${idx}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="space-y-2 pt-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={generateLoading}
                         onChange={e => setGenerateLoading(e.target.checked)}
                         data-testid="bulk-toggle-loading" />
                  <span>Générer les bons de chargement (BC)</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={generateUnloading}
                         onChange={e => setGenerateUnloading(e.target.checked)}
                         data-testid="bulk-toggle-unloading" />
                  <span>Générer les bons de déchargement (BD)</span>
                  <span className="text-xs text-gray-500">— par défaut générés à la livraison</span>
                </label>
                {generateLoading && generateUnloading && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer pl-6">
                    <input type="checkbox" checked={pairBcBd}
                           onChange={e => setPairBcBd(e.target.checked)}
                           data-testid="bulk-toggle-pair" />
                    <span>Lier BC↔BD du même voyage (pairId)</span>
                  </label>
                )}
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-900">
                <strong>{totalBons}</strong> bon(s) seront générés.
                Les bons déjà existants pour ces (camion, rotation) seront automatiquement ignorés.
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  data-testid="bulk-cancel">
            Annuler
          </button>
          <button onClick={handleSubmit}
                  disabled={submitting || loading || totalBons === 0}
                  className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  data-testid="bulk-submit">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Générer {totalBons > 0 && `(${totalBons})`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkGenerateNotesDrawer;
